import { Directive, ElementRef, HostBinding, HostListener, inject, input } from '@angular/core';
import { HxBaseControl } from '../../base/hx-base-control';
import { MAT_CHIP } from './chip-tokens';

/**
 * Base directive for interactive chip action regions.
 *
 * Implements the action-based architecture pattern from Angular Material for managing distinct interactive regions
 * within a chip (primary action vs. trailing actions).
 *
 * Internal directive - not intended for direct use by consumers. Use typed directives instead: HxChipAvatar,
 * HxChipTrailingIcon, HxChipRemove.
 *
 * @docs-private
 * @internal
 */
@Directive({
  selector: '[hxChipAction]',
  standalone: true,
  host: {
    class: 'hx-chip-action',
    '[class.hx-chip-action--primary]': '_isPrimary',
    '[class.hx-chip-action--trailing]': '!_isPrimary',
  },
})
export class HxChipAction extends HxBaseControl {
  protected readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  protected readonly _parentChip = inject<{
    _handlePrimaryActionInteraction(): void;
    remove(): void;
    disabled: boolean;
  }>(MAT_CHIP, { optional: true });

  /** Whether this is the primary action in the chip. */
  readonly _isPrimary = input<boolean>(true);

  /** Tab index of the action. */
  override readonly tabIndex = input<number>(-1);

  @HostBinding('attr.tabindex')
  get _tabindex(): string | null {
    return this.disabledState() ? null : this.tabIndex().toString();
  }

  @HostBinding('attr.disabled')
  get _disabled(): string | null {
    return this.disabledState() || this._parentChip?.disabled ? '' : null;
  }

  @HostListener('click', ['$event'])
  _handleClick(event: MouseEvent) {
    if (!this.disabledState() && this._isPrimary() && this._parentChip) {
      event.preventDefault();
      this._parentChip._handlePrimaryActionInteraction();
    }
  }

  @HostListener('keydown', ['$event'])
  _handleKeydown(event: KeyboardEvent) {
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      !this.disabledState() &&
      this._isPrimary() &&
      this._parentChip
    ) {
      event.preventDefault();
      this._parentChip._handlePrimaryActionInteraction();
    }
  }

  /** Focus this action. */
  focus(): void {
    this._elementRef.nativeElement.focus();
  }
}
